import { useEffect, useId, useRef, useState } from 'react'
import { Autocomplete } from '@base-ui/react/autocomplete'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { Building2, ChevronDown, FolderInput, Plus } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Breadcrumb } from '@/ui/breadcrumb'
import { Button } from '@/ui/button'
import { buttonVariants } from '@/ui/button-variants'
import { Popover, PopoverContent, PopoverTrigger } from '@/ui/popover'
import { ResizeHandle } from '@/ui/resize-handle'
import { Switch } from '@/ui/switch'
import { useAssistantBrowserCommand } from '@/lib/assistant-browser-commands'
import { assistantBrowserCommands } from '@/lib/assistant-browser-commands.generated'

import { type Entity } from '@/modules/hierarchy/api/hierarchy-api'

import {
  useCanEditEntity,
  useRoots,
  useSelectedEntity,
} from '../../lib/entity-queries'
import {
  useEntityRecents,
  type EntityRef,
} from '../../lib/use-entity-recents'
import { useEntitySearchBox } from './use-entity-search-box'
import {
  PANEL_HEIGHT,
  PANEL_WIDTH,
  useEntitySelectorSize,
} from './use-entity-selector-size'
import { useTreeKeyboard } from './use-tree-keyboard'
import { CreateEntityForm } from './create-entity-form'
import {
  EntitySearchInput,
  EntitySearchResults,
  searchStatusText,
} from './entity-search-results'
import { EntityTreeNode } from './entity-tree-node'
import { MoveEntityForm } from './move-entity-form'
import { ResizeCorner } from './resize-corner'
import { pathChildMap } from './path-child'
import { rowClass, sectionLabel } from './styles'

type Mode = 'browse' | 'create' | 'move'

export function selectEntityRoute(state: {
  matches: { fullPath?: string }[]
}): string {
  const fullPath = state.matches.at(-1)?.fullPath
  if (!fullPath?.includes('$entityId') || fullPath === '/$entityId/') {
    return '/$entityId/dashboard'
  }

  const segments = fullPath.split('/')
  const entitySegment = segments.indexOf('$entityId')
  const resourceSegment = segments.findIndex(
    (segment, index) => index > entitySegment && segment.startsWith('$'),
  )

  if (resourceSegment === -1) return fullPath

  const collectionRoute = segments.slice(0, resourceSegment).join('/')
  return collectionRoute === '/$entityId'
    ? '/$entityId/dashboard'
    : collectionRoute
}

// Create parent is whichever entity the route is scoped to (`/$entityId/...`);
// entity data comes from the shared Query cache via useSelectedEntity.
export function EntitySelector() {
  const navigate = useNavigate()
  const toRoute = useRouterState({
    // The deepest match can be a pathless layout (e.g. the Access section),
    // whose fullPath isn't a navigable target, or the home page (/) which has
    // no $entityId segment — fall back to a concrete route in both cases.
    select: selectEntityRoute,
  })
  const {
    entityId,
    entity: entityQuery,
    ancestors: ancestorsQuery,
  } = useSelectedEntity()
  const canEdit = useCanEditEntity()
  const {
    style: panelStyle,
    size: panelSize,
    setSize: setPanelSize,
    setWidth: setPanelWidth,
    setHeight: setPanelHeight,
  } = useEntitySelectorSize()
  const { recents, remember: rememberEntity } = useEntityRecents()
  const [open, setOpen] = useState(false)
  const rootsQuery = useRoots(open)
  const [mode, setMode] = useState<Mode>('browse')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [searchEverywhere, setSearchEverywhere] = useState(true)

  const entity = entityQuery.data ?? null
  const ancestors = ancestorsQuery.data?.items ?? []
  const breadcrumb = ancestors.map((a) => ({ label: a.name }))
  const roots = rootsQuery.data ?? []
  // /entities/roots omits roots the caller cannot read as roots, so a scoped user
  // may not see their own tree-top there. Inject ancestors[0] when it is missing.
  const treeTop = ancestors[0]
  const treeRoots: Entity[] =
    treeTop &&
    treeTop.entityId !== entityId &&
    !roots.some((r) => r.entityId === treeTop.entityId)
      ? [
          ...roots,
          {
            entityId: treeTop.entityId,
            name: treeTop.name,
            parentEntityId: null,
            hasChildren: treeTop.hasChildren,
            // Injected from the ancestor chain, which doesn't carry the user-management
            // flag; the badge resolves once children load from /entities.
            canManageUsers: false,
          },
        ]
      : roots
  // Roots still loading or failed → fall back to the selected subtree.
  const searchRootIds =
    searchEverywhere && treeRoots.length > 0
      ? treeRoots.map((root) => root.entityId)
      : undefined
  const {
    query,
    setQuery,
    isSearching,
    search,
    results: searchResults,
    pending: searchPending,
  } = useEntitySearchBox(entityId, searchRootIds)
  const pathChild = pathChildMap(ancestors)
  const treeRef = useRef<HTMLDivElement>(null)
  const treeLabelId = useId()
  const tree = useTreeKeyboard({
    treeRef,
    onToggleExpanded: toggleExpanded,
    onSelect: handleSelect,
    fallbackActiveId: entityId ?? treeRoots[0]?.entityId ?? null,
  })
  const loading = entityQuery.isLoading || ancestorsQuery.isLoading
  const loadError = entityQuery.error ?? ancestorsQuery.error

  const panelRef = useRef<HTMLDivElement>(null)

  useAssistantBrowserCommand(assistantBrowserCommands.switchEntity, async ({ entityId: nextId, name }) => {
    if (!name.trim()) return false
    rememberEntity({ id: nextId, name })
    handleOpenChange(false)
    try {
      await navigate({ to: toRoute, params: (previous) => ({ ...previous, entityId: nextId }) })
      return true
    } catch {
      return false
    }
  })
  useAssistantBrowserCommand(assistantBrowserCommands.hierarchyNode, ({ entityId: targetId, expanded: desired }) => {
    if (!open) return false
    const node = treeRef.current?.querySelector<HTMLElement>(`[role="treeitem"][data-entity-id="${CSS.escape(targetId)}"]`)
    if (!node || node.getAttribute('aria-expanded') === null) return false
    setExpanded((current) => {
      const next = new Set(current)
      if (desired) next.add(targetId)
      else next.delete(targetId)
      return next
    })
    node.focus()
    return true
  })

  function renderedPanel(): { width: number; height: number } | undefined {
    const box = panelRef.current?.getBoundingClientRect()
    if (!box || box.width <= 0 || box.height <= 0) return undefined
    return { width: Math.round(box.width), height: Math.round(box.height) }
  }

  const createButtonRef = useRef<HTMLButtonElement>(null)
  const moveButtonRef = useRef<HTMLButtonElement>(null)
  const restoreFocusTo = useRef<Exclude<Mode, 'browse'> | null>(null)

  useEffect(() => {
    if (mode === 'browse' && restoreFocusTo.current) {
      const target =
        restoreFocusTo.current === 'move'
          ? moveButtonRef.current
          : createButtonRef.current
      restoreFocusTo.current = null
      target?.focus()
    }
  }, [mode])

  function enterMode(next: Exclude<Mode, 'browse'>) {
    restoreFocusTo.current = next
    setMode(next)
  }

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (!next) {
      setMode('browse')
      setQuery('')
      return
    }
    // Open drilled-in to the current entity (as the mock shows). Each expanded
    // level fetches one page of children — bounded by depth, never the whole
    // tree. Uses already-cached ancestors; deeper drilling is on the user.
    const pathIds = ancestors.map((a) => a.entityId)
    if (pathIds.length > 0) {
      setExpanded((prev) => new Set([...prev, ...pathIds]))
    }
  }

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function handleSelect(ref: EntityRef) {
    if (ref.id === entityId) {
      handleOpenChange(false)
      return
    }
    rememberEntity(ref)
    handleOpenChange(false)
    void navigate({ to: toRoute, params: (prev) => ({ ...prev, entityId: ref.id }) })
  }

  const hasEntity = entityId !== null && entity !== null
  const canCreate = hasEntity && canEdit
  const canMove = hasEntity && canEdit
  const triggerLabel =
    entity?.name ??
    (loading
      ? 'Loading…'
      : loadError
        ? 'Entity unavailable'
        : 'Select an entity')

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger
        className={cn(
          buttonVariants({ variant: 'chrome', size: 'sm' }),
          'gap-1.5 font-medium',
        )}
        aria-label="Select entity"
      >
        <Building2 className="size-3.5 opacity-65" />
        <span className="max-w-[14rem] truncate">{triggerLabel}</span>
        <ChevronDown className="size-3.5 opacity-65" />
      </PopoverTrigger>
      <PopoverContent
        ref={panelRef}
        align="start"
        sideOffset={6}
        style={panelStyle}
        className={cn(
          'relative max-h-[var(--available-height)] w-[min(var(--panel-width,28rem),var(--available-width))] gap-3 p-3',
          mode === 'browse'
            ? 'h-[min(var(--panel-height,clamp(20rem,75vh,56.25rem)),var(--available-height))] overflow-hidden'
            : 'overflow-y-auto',
        )}
      >
        {mode === 'browse' && (
          <>
            <ResizeHandle
              label="Resize entity selector width"
              orientation="vertical"
              value={panelSize.width}
              min={PANEL_WIDTH.min}
              max={PANEL_WIDTH.max}
              className="right-0"
              measure={() => renderedPanel()?.width}
              onChange={setPanelWidth}
            />
            <ResizeHandle
              label="Resize entity selector height"
              orientation="horizontal"
              value={panelSize.height}
              min={PANEL_HEIGHT.min}
              max={PANEL_HEIGHT.max}
              className="bottom-0"
              measure={() => renderedPanel()?.height}
              onChange={setPanelHeight}
            />
            <ResizeCorner
              size={panelSize}
              measure={renderedPanel}
              onChange={setPanelSize}
            />
          </>
        )}
        {mode === 'create' && entity ? (
          <CreateEntityForm
            parentId={entity.entityId}
            parentName={entity.name}
            onCancel={() => setMode('browse')}
            onSuccess={() => handleOpenChange(false)}
          />
        ) : mode === 'move' && entity ? (
          <MoveEntityForm
            entityId={entity.entityId}
            entityName={entity.name}
            currentParentId={entity.parentEntityId}
            onCancel={() => setMode('browse')}
            onSuccess={() => handleOpenChange(false)}
          />
        ) : (
          <Autocomplete.Root
            mode="none"
            open={isSearching ? search.error == null : recents.length > 0}
            onOpenChange={() => {}}
            value={query}
            onValueChange={setQuery}
          >
            <div className="flex shrink-0 items-center gap-1.5">
              <div className="min-w-0 flex-1">
                <EntitySearchInput query={query} onValueChange={setQuery} />
              </div>
              {canCreate && (
                <Button
                  ref={createButtonRef}
                  type="button"
                  variant="outline"
                  size="lg"
                  aria-label="Create entity"
                  onClick={() => enterMode('create')}
                >
                  <Plus data-icon="inline-start" />
                  New
                </Button>
              )}
              {canMove && (
                <Button
                  ref={moveButtonRef}
                  type="button"
                  variant="outline"
                  size="lg"
                  aria-label="Move entity"
                  onClick={() => enterMode('move')}
                >
                  <FolderInput data-icon="inline-start" />
                  Move
                </Button>
              )}
            </div>

            {isSearching && entity && (
              <div className="text-muted-foreground flex shrink-0 items-center gap-1.5 px-2 text-xs">
                <Switch
                  aria-label={`Only within ${entity.name}`}
                  checked={!searchEverywhere}
                  onCheckedChange={(onlyHere) => setSearchEverywhere(!onlyHere)}
                />
                <span className="truncate">Only within {entity.name}</span>
              </div>
            )}

            <Autocomplete.Status className="text-muted-foreground shrink-0 px-2 text-xs empty:sr-only">
              {searchStatusText(
                isSearching,
                searchPending || (searchEverywhere && rootsQuery.isLoading),
                search.error != null,
                searchResults.length,
              )}
            </Autocomplete.Status>

            {isSearching ? (
              <EntitySearchResults
                height="fill"
                results={searchResults}
                error={search.error}
                hasNextPage={search.data?.pageInfo.hasNextPage ?? false}
                selectedId={entityId}
                onSelect={handleSelect}
                onRetry={() => void search.refetch()}
              />
            ) : (
              <>
                <div className="shrink-0">
                  <p className="text-muted-foreground mb-1 text-[10px] font-semibold tracking-wider uppercase">
                    Current
                  </p>
                  {loading ? (
                    <p className="text-muted-foreground text-xs">Loading…</p>
                  ) : breadcrumb.length > 0 ? (
                    <Breadcrumb segments={breadcrumb} />
                  ) : (
                    <p className="text-muted-foreground text-xs">
                      {loadError
                        ? loadError.message
                        : 'Open an entity to manage its hierarchy.'}
                    </p>
                  )}
                </div>

                {recents.length > 0 && (
                  <div className="flex shrink-0 flex-col gap-0.5">
                    <p className={sectionLabel}>Recent</p>
                    <Autocomplete.List
                      aria-label="Recent entities"
                      className="flex flex-col gap-0.5"
                    >
                      {recents.map((r) => (
                        <Autocomplete.Item
                          key={`recent-${r.id}`}
                          value={`recent-${r.id}`}
                          data-selected={r.id === entityId}
                          onClick={() => handleSelect(r)}
                          className={rowClass}
                        >
                          <Building2 className="text-muted-foreground size-3.5" />
                          <span className="truncate">{r.name}</span>
                        </Autocomplete.Item>
                      ))}
                    </Autocomplete.List>
                  </div>
                )}

                {(rootsQuery.isLoading ||
                  rootsQuery.isError ||
                  treeRoots.length > 0) && (
                  <div className="flex min-h-0 grow flex-col gap-0.5 overflow-y-auto">
                    <p id={treeLabelId} className={sectionLabel}>
                      All entities
                    </p>
                    {rootsQuery.isLoading ? (
                      <p className="text-muted-foreground text-xs">Loading…</p>
                    ) : rootsQuery.isError ? (
                      <div role="alert" className="flex items-center gap-2 py-0.5">
                        <span className="text-destructive text-xs">
                          {rootsQuery.error.message}
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="xs"
                          onClick={() => void rootsQuery.refetch()}
                        >
                          Retry
                        </Button>
                      </div>
                    ) : (
                      <div
                        ref={treeRef}
                        role="tree"
                        tabIndex={-1}
                        aria-labelledby={treeLabelId}
                        onClick={tree.onClick}
                        onKeyDown={tree.onKeyDown}
                        onFocus={tree.onFocus}
                        className="flex flex-col gap-0.5"
                      >
                        {treeRoots.map((r) => (
                          <EntityTreeNode
                            key={r.entityId}
                            entityId={r.entityId}
                            name={r.name}
                            hasChildren={r.hasChildren}
                            canManageUsers={r.canManageUsers}
                            depth={0}
                            parentId={null}
                            expanded={expanded}
                            selectedId={entityId}
                            activeId={tree.activeId}
                            pathChild={pathChild}
                            registerNode={tree.registerNode}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </Autocomplete.Root>
        )}
      </PopoverContent>
    </Popover>
  )
}
